import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ref, push } from "firebase/database";
import { db, auth } from "../../firebaseConfig";
import { colors, radius, spacing, elevation } from "../../utils/designSystem";
import { storageKey, finishedKey } from "../../utils/tourUtils";

// Localized strings
const STRINGS = {
  en: {
    title: "You found Cofresí's Treasure!",
    subtitle: "Well played!",
    exit: "Exit",
    howWasTour: "How was your tour?",
    feedbackHelps: "Your feedback helps us improve the experience!",
    rating: "Rating",
    loveTheTour: "Love the tour? Share your experience!",
    helpOthersDiscover: "Help others discover this adventure by leaving a review on Google.",
    leaveGoogleReview: "⭐ Leave a Google Review",
    opensNewTab: "Opens in a new tab",
    whatCouldImprove: "What could we improve? (optional)",
    feedbackPlaceholder: "Your feedback helps us make the experience better...",
    freeSticker: "Get a FREE Collectible Sticker!",
    stickerDescription: "Share your US mailing address and we'll send you a free San Juan sticker! (optional)",
    name: "Name",
    namePlaceholder: "Your full name",
    address1: "Address Line 1",
    address1Placeholder: "Street address",
    address2: "Address Line 2 (optional)",
    address2Placeholder: "Apt, suite, unit, etc.",
    city: "City",
    cityPlaceholder: "City",
    state: "State",
    statePlaceholder: "CA",
    zip: "ZIP Code",
    zipPlaceholder: "12345",
    submitting: "Submitting...",
    submitFeedback: "Submit Feedback",
    skipReviewed: "Skip (Already reviewed on Google)",
    somethingWrong: "Something went wrong. Please try again.",
    thankYou: "Thank you for your feedback!",
    appreciateTime: "We appreciate you taking the time to share your thoughts.",
    restartTour: "Restart Tour",
    selectRating: "Please select a star rating before submitting!",
  },
  es: {
    title: "¡Encontraste el Tesoro de Cofresí!",
    subtitle: "¡Bien jugado!",
    exit: "Salir",
    howWasTour: "¿Cómo estuvo tu tour?",
    feedbackHelps: "¡Tu opinión nos ayuda a mejorar la experiencia!",
    rating: "Calificación",
    loveTheTour: "¿Te encantó el tour? ¡Comparte tu experiencia!",
    helpOthersDiscover: "Ayuda a otros a descubrir esta aventura dejando una reseña en Google.",
    leaveGoogleReview: "⭐ Dejar una Reseña en Google",
    opensNewTab: "Se abre en una nueva pestaña",
    whatCouldImprove: "¿Qué podríamos mejorar? (opcional)",
    feedbackPlaceholder: "Tu opinión nos ayuda a mejorar la experiencia...",
    freeSticker: "¡Obtén una Calcomanía Coleccionable GRATIS!",
    stickerDescription: "Comparte tu dirección en EE. UU. y te enviaremos una calcomanía gratis de San Juan. (opcional)",
    name: "Nombre",
    namePlaceholder: "Tu nombre completo",
    address1: "Dirección Línea 1",
    address1Placeholder: "Dirección de la calle",
    address2: "Dirección Línea 2 (opcional)",
    address2Placeholder: "Apt, suite, unidad, etc.",
    city: "Ciudad",
    cityPlaceholder: "Ciudad",
    state: "Estado",
    statePlaceholder: "PR",
    zip: "Código Postal",
    zipPlaceholder: "00901",
    submitting: "Enviando...",
    submitFeedback: "Enviar Opinión",
    skipReviewed: "Omitir (Ya dejé reseña en Google)",
    somethingWrong: "Algo salió mal. Por favor intenta de nuevo.",
    thankYou: "¡Gracias por tu opinión!",
    appreciateTime: "Apreciamos que te hayas tomado el tiempo de compartir tus comentarios.",
    restartTour: "Reiniciar Tour",
    selectRating: "¡Por favor selecciona una calificación antes de enviar!",
  }
};

export function FinishScreen({ tourSlug = "san-juan", onRestart, lang = "en" }) {
  const t = STRINGS[lang] || STRINGS.en;
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submissionStatus, setSubmissionStatus] = useState("idle"); // idle, submitting, success, error
  const [mailingAddress, setMailingAddress] = useState({
    name: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: ""
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);
    const onResize = () => { width = canvas.width = window.innerWidth; height = canvas.height = window.innerHeight; };
    window.addEventListener('resize', onResize);
    const particles = Array.from({ length: 180 }, () => ({
      x: Math.random() * width,
      y: -20 - Math.random() * height,
      vx: (Math.random() - 0.5) * 4,
      vy: 2 + Math.random() * 3,
      size: 6 + Math.random() * 6,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.2,
    }));
    let raf;
    const confettiColors = [colors.treasure.gold, colors.accent.main, colors.primary.main, colors.accent.light, colors.error.main, colors.treasure.amber];
    const tick = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.vy += 0.05; p.rot += p.vr;
        if (p.y > height + 20) { p.y = -20; p.x = Math.random() * width; p.vy = 2 + Math.random() * 3; }
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
        ctx.fillStyle = confettiColors[(p.size | 0) % confettiColors.length];
        ctx.fillRect(-p.size/2, -p.size/2, p.size, p.size); ctx.restore();
      });
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); };
  }, []);

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      alert(t.selectRating);
      return;
    }

    setSubmissionStatus("submitting");

    try {
      const tourSlug = "san-juan";
      const feedbackRef = ref(db, `feedback/${tourSlug}`);

      // Include mailing address only if at least name and address1 are provided
      const hasMailingAddress = mailingAddress.name.trim() && mailingAddress.address1.trim();

      const feedbackData = {
        tourId: tourSlug,
        tourSlug: tourSlug,
        rating: rating,
        comment: comment.trim() || null,
        userEmail: auth.currentUser?.email || "anonymous",
        completedAt: Date.now(),
        timestamp: new Date().toISOString(),
        ...(hasMailingAddress && {
          mailingAddress: {
            name: mailingAddress.name.trim(),
            address1: mailingAddress.address1.trim(),
            address2: mailingAddress.address2.trim() || null,
            city: mailingAddress.city.trim(),
            state: mailingAddress.state.trim(),
            zip: mailingAddress.zip.trim()
          }
        })
      };

      await push(feedbackRef, feedbackData);
      setSubmissionStatus("success");
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      setSubmissionStatus("error");
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <canvas ref={canvasRef} className="pointer-events-none fixed inset-0" />
      <div className="relative z-10 mx-auto max-w-xl px-4 py-16 text-center">
        <div className="mx-auto grid h-32 w-32 place-items-center rounded-full bg-amber-200 shadow-inner">
          <span className="text-6xl" role="img" aria-label="Treasure Chest">🏆</span>
        </div>
        <h1 className="font-libre mt-6 text-3xl font-extrabold tracking-tight" style={{ color: 'black' }}>{t.title}</h1>
        <p className="mt-2 text-slate-700">{t.subtitle}</p>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => navigate('/')}
            className="block w-full max-w-xs font-semibold"
            style={{
              borderRadius: radius['2xl'],
              padding: `${spacing[3]} ${spacing[4]}`,
              border: `1px solid ${colors.neutral[300]}`,
              background: colors.neutral.white,
              boxShadow: elevation.md,
            }}
          >
            {t.exit}
          </button>
        </div>

        {/* Feedback Form */}
        {submissionStatus !== "success" && (
          <div
            className="mt-12 text-left"
            style={{
              borderRadius: radius.xl,
              padding: spacing[6],
              background: colors.neutral.white,
              border: `1px solid ${colors.neutral[200]}`,
              boxShadow: elevation.md,
            }}
          >
            <h2 className="text-xl font-bold mb-2" style={{ color: colors.neutral[900] }}>
              {t.howWasTour}
            </h2>
            <p className="text-sm mb-4" style={{ color: colors.neutral[600] }}>
              {t.feedbackHelps}
            </p>

            <form onSubmit={handleSubmitFeedback}>
              {/* Star Rating */}
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2" style={{ color: colors.neutral[700] }}>
                  {t.rating} <span style={{ color: colors.error.main }}>*</span>
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="text-3xl transition-transform hover:scale-110"
                      style={{
                        cursor: 'pointer',
                        filter: (hoverRating >= star || rating >= star) ? 'none' : 'grayscale(100%)',
                      }}
                    >
                      {(hoverRating >= star || rating >= star) ? '⭐' : '☆'}
                    </button>
                  ))}
                </div>
              </div>

              {/* For 5 stars: Google Review Button */}
              {rating === 5 && (
                <div className="mb-4">
                  <div
                    className="p-4 rounded-lg text-center"
                    style={{
                      background: 'linear-gradient(135deg, #FFF4E6 0%, #FFE8CC 100%)',
                      border: `2px solid ${colors.treasure.gold}`
                    }}
                  >
                    <div className="text-3xl mb-2">🌟</div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: colors.neutral[900] }}>
                      {t.loveTheTour}
                    </h3>
                    <p className="text-sm mb-4" style={{ color: colors.neutral[700] }}>
                      {t.helpOthersDiscover}
                    </p>
                    <a
                      href="https://g.page/r/CVmOb3BkuMKREBM/review"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block w-full font-semibold transition-all hover:scale-105"
                      style={{
                        borderRadius: radius.full,
                        padding: `${spacing[3]} ${spacing[4]}`,
                        background: colors.treasure.gold,
                        color: colors.neutral[900],
                        textDecoration: 'none',
                        boxShadow: elevation.md,
                      }}
                      onClick={() => {
                        // Track 5-star rating in Firebase before redirecting
                        const tourSlug = "san-juan";
                        const feedbackRef = ref(db, `feedback/${tourSlug}`);
                        const feedbackData = {
                          tourId: tourSlug,
                          tourSlug: tourSlug,
                          rating: rating,
                          comment: "Redirected to Google Review",
                          userEmail: auth.currentUser?.email || "anonymous",
                          completedAt: Date.now(),
                          timestamp: new Date().toISOString(),
                          redirectedToGoogle: true
                        };
                        push(feedbackRef, feedbackData).catch(err => console.error("Failed to track feedback:", err));
                      }}
                    >
                      {t.leaveGoogleReview}
                    </a>
                    <p className="text-xs mt-3" style={{ color: colors.neutral[600] }}>
                      {t.opensNewTab}
                    </p>
                  </div>
                </div>
              )}

              {/* For 1-4 stars: Comment Textarea */}
              {rating > 0 && rating < 5 && (
                <div className="mb-4">
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.neutral[700] }}>
                    {t.whatCouldImprove}
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t.feedbackPlaceholder}
                    rows={3}
                    className="w-full text-sm"
                    style={{
                      borderRadius: radius.lg,
                      padding: spacing[3],
                      border: `1px solid ${colors.neutral[300]}`,
                      background: colors.neutral[50],
                      color: colors.neutral[900],
                      resize: 'vertical',
                    }}
                  />
                </div>
              )}

              {/* Mailing Address for Free Sticker */}
              <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: colors.treasure.lightGold, border: `2px dashed ${colors.treasure.gold}` }}>
                <div className="flex items-start gap-2 mb-3">
                  <span className="text-2xl">🎁</span>
                  <div>
                    <h3 className="text-sm font-bold" style={{ color: colors.neutral[900] }}>
                      {t.freeSticker}
                    </h3>
                    <p className="text-xs mt-1" style={{ color: colors.neutral[700] }}>
                      {t.stickerDescription}
                    </p>
                  </div>
                </div>

                {/* Name */}
                <div className="mb-3">
                  <label className="block text-xs font-semibold mb-1" style={{ color: colors.neutral[700] }}>
                    {t.name}
                  </label>
                  <input
                    type="text"
                    value={mailingAddress.name}
                    onChange={(e) => setMailingAddress({...mailingAddress, name: e.target.value})}
                    placeholder={t.namePlaceholder}
                    className="w-full text-sm"
                    style={{
                      borderRadius: radius.md,
                      padding: spacing[2],
                      border: `1px solid ${colors.neutral[300]}`,
                      background: colors.neutral.white,
                      color: colors.neutral[900],
                    }}
                  />
                </div>

                {/* Address Line 1 */}
                <div className="mb-3">
                  <label className="block text-xs font-semibold mb-1" style={{ color: colors.neutral[700] }}>
                    {t.address1}
                  </label>
                  <input
                    type="text"
                    value={mailingAddress.address1}
                    onChange={(e) => setMailingAddress({...mailingAddress, address1: e.target.value})}
                    placeholder={t.address1Placeholder}
                    className="w-full text-sm"
                    style={{
                      borderRadius: radius.md,
                      padding: spacing[2],
                      border: `1px solid ${colors.neutral[300]}`,
                      background: colors.neutral.white,
                      color: colors.neutral[900],
                    }}
                  />
                </div>

                {/* Address Line 2 */}
                <div className="mb-3">
                  <label className="block text-xs font-semibold mb-1" style={{ color: colors.neutral[700] }}>
                    {t.address2}
                  </label>
                  <input
                    type="text"
                    value={mailingAddress.address2}
                    onChange={(e) => setMailingAddress({...mailingAddress, address2: e.target.value})}
                    placeholder={t.address2Placeholder}
                    className="w-full text-sm"
                    style={{
                      borderRadius: radius.md,
                      padding: spacing[2],
                      border: `1px solid ${colors.neutral[300]}`,
                      background: colors.neutral.white,
                      color: colors.neutral[900],
                    }}
                  />
                </div>

                {/* City, State, Zip */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-xs font-semibold mb-1" style={{ color: colors.neutral[700] }}>
                      {t.city}
                    </label>
                    <input
                      type="text"
                      value={mailingAddress.city}
                      onChange={(e) => setMailingAddress({...mailingAddress, city: e.target.value})}
                      placeholder={t.cityPlaceholder}
                      className="w-full text-sm"
                      style={{
                        borderRadius: radius.md,
                        padding: spacing[2],
                        border: `1px solid ${colors.neutral[300]}`,
                        background: colors.neutral.white,
                        color: colors.neutral[900],
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: colors.neutral[700] }}>
                      {t.state}
                    </label>
                    <input
                      type="text"
                      value={mailingAddress.state}
                      onChange={(e) => setMailingAddress({...mailingAddress, state: e.target.value.toUpperCase()})}
                      placeholder={t.statePlaceholder}
                      maxLength={2}
                      className="w-full text-sm uppercase"
                      style={{
                        borderRadius: radius.md,
                        padding: spacing[2],
                        border: `1px solid ${colors.neutral[300]}`,
                        background: colors.neutral.white,
                        color: colors.neutral[900],
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: colors.neutral[700] }}>
                      {t.zip}
                    </label>
                    <input
                      type="text"
                      value={mailingAddress.zip}
                      onChange={(e) => setMailingAddress({...mailingAddress, zip: e.target.value})}
                      placeholder={t.zipPlaceholder}
                      maxLength={10}
                      className="w-full text-sm"
                      style={{
                        borderRadius: radius.md,
                        padding: spacing[2],
                        border: `1px solid ${colors.neutral[300]}`,
                        background: colors.neutral.white,
                        color: colors.neutral[900],
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button - Only show for 1-4 star ratings */}
              {rating > 0 && rating < 5 && (
                <button
                  type="submit"
                  disabled={submissionStatus === "submitting"}
                  className="w-full font-semibold transition-all"
                  style={{
                    borderRadius: radius.full,
                    padding: `${spacing[3]} ${spacing[4]}`,
                    background: submissionStatus === "submitting" ? colors.neutral[400] : colors.success.main,
                    color: colors.neutral.white,
                    border: 'none',
                    cursor: submissionStatus === "submitting" ? 'not-allowed' : 'pointer',
                    opacity: submissionStatus === "submitting" ? 0.7 : 1,
                  }}
                >
                  {submissionStatus === "submitting" ? t.submitting : t.submitFeedback}
                </button>
              )}

              {/* Skip button for 5-star ratings (after Google redirect) */}
              {rating === 5 && (
                <button
                  type="button"
                  onClick={() => setSubmissionStatus("success")}
                  className="w-full font-semibold transition-all"
                  style={{
                    borderRadius: radius.full,
                    padding: `${spacing[3]} ${spacing[4]}`,
                    background: colors.neutral[200],
                    color: colors.neutral[700],
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {t.skipReviewed}
                </button>
              )}

              {submissionStatus === "error" && (
                <p className="mt-3 text-sm text-center" style={{ color: colors.error.main }}>
                  {t.somethingWrong}
                </p>
              )}
            </form>
          </div>
        )}

        {/* Success Message */}
        {submissionStatus === "success" && (
          <div
            className="mt-12 text-center"
            style={{
              borderRadius: radius.xl,
              padding: spacing[6],
              background: colors.success.bg,
              border: `2px solid ${colors.success.main}`,
            }}
          >
            <div className="text-4xl mb-3">✨</div>
            <h2 className="text-xl font-bold mb-2" style={{ color: colors.success.dark }}>
              {t.thankYou}
            </h2>
            <p className="text-sm" style={{ color: colors.neutral[700] }}>
              {t.appreciateTime}
            </p>
          </div>
        )}

        {/* Restart Tour Button */}
        <div className="mt-16 pt-8 border-t border-slate-200">
          <button
            onClick={() => {
              // Clear all tour progress from localStorage
              localStorage.removeItem(finishedKey(tourSlug));
              localStorage.removeItem(storageKey(tourSlug));
              localStorage.removeItem(`tib:${tourSlug}:phase`);
              // Clear individual step answers
              for (let i = 1; i <= 12; i++) {
                localStorage.removeItem(`tib:${tourSlug}:step-${i}`);
              }
              // Call onRestart if provided, otherwise reload
              if (onRestart) {
                onRestart();
              } else {
                window.location.reload();
              }
            }}
            className="text-sm font-medium transition-all hover:underline"
            style={{
              color: colors.neutral[500],
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {t.restartTour}
          </button>
        </div>
      </div>
    </div>
  );
}
